import { useState, useEffect, useCallback, useRef } from 'react';
import { clientLinkUrl } from '../lib/hostPolicy';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { isTaskOverdue, taskDueDate } from '../utils/deadlineDates';
import {
  CrmDeal, CrmDealType, DealStage, DealPriority, Activity, ActivityType,
  DealContactLink, DealStageHistoryEntry, DealRoomDocument, DealRoomInvite,
  DealRoomActivityEntry, DealTask, TaskPlaybook, DealRoomDocumentCategory, OfficialChecklistInfo,
} from '../types';
import MasterLinkPrompt from '../components/MasterLinkPrompt';
import { Card, CardHeader, CardTitle, CardContent, Button, Badge, Tag, Dialog} from '../components/ui';
import DealContracts from '../components/DealContracts';
import LoiGeneratorDialog, { type LoiPrefill } from '../components/LoiGeneratorDialog';
import { recordFactsOf } from '../components/loi/recordFacts';
import { recordSiteSize } from '../components/loi/recordSiteSize';
import ProposalGeneratorDialog, { type ProposalPrefill } from '../components/ProposalGeneratorDialog';
import BrochureGeneratorDialog, { type BrochurePrefill } from '../components/BrochureGeneratorDialog';
import DocumentViewer from '../components/DocumentViewer';
import OpenPdfButton from '../components/documents/OpenPdfButton';
import Layout from '../components/Layout';
import DealActionStrip from '../components/DealActionStrip';
import DealPropertyPanel from '../components/DealPropertyPanel';
import Breadcrumbs from '../components/Breadcrumbs';
import DeadlinesPanel from '../components/escrow/DeadlinesPanel';
import DealCockpit from '../components/escrow/DealCockpit';
import { useSetPageSubject, type PageSubject } from '../contexts/PageSubjectContext';
import { usePageAction, type PageActionFields } from '../contexts/PageActionContext';

type DealTab = 'overview' | 'deal-room' | 'tasks' | 'deadlines';

const STAGES: { value: DealStage; label: string }[] = [
  { value: 'prospecting', label: 'Prospecting' }, { value: 'qualification', label: 'Qualification' },
  { value: 'proposal', label: 'Proposal' }, { value: 'negotiation', label: 'Negotiation' },
  { value: 'under_contract', label: 'Under Contract' }, { value: 'due_diligence', label: 'Due Diligence' },
  { value: 'closing', label: 'Closing' }, { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
];

const DEAL_TYPES: { value: CrmDealType; label: string }[] = [
  { value: 'sale', label: 'Sale' }, { value: 'lease', label: 'Lease' },
  { value: 'listing', label: 'Listing' }, { value: 'acquisition', label: 'Acquisition' },
  { value: 'disposition', label: 'Disposition' },
];

const PRIORITIES: { value: DealPriority; label: string }[] = [
  { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' }, { value: 'urgent', label: 'Urgent' },
];

const ACTIVITY_TYPES: { value: ActivityType; label: string }[] = [
  { value: 'call', label: 'Call' }, { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Meeting' }, { value: 'note', label: 'Note' },
  { value: 'task', label: 'Task' }, { value: 'site_visit', label: 'Site Visit' },
  { value: 'other', label: 'Other' },
];

const DOC_CATEGORIES: { value: DealRoomDocumentCategory; label: string }[] = [
  { value: 'loi', label: 'LOI' }, { value: 'psa', label: 'PSA' },
  { value: 'title', label: 'Title' }, { value: 'environmental', label: 'Environmental' },
  { value: 'appraisal', label: 'Appraisal' }, { value: 'lease', label: 'Lease' },
  { value: 'lease_proposal', label: 'Lease proposal' },
  { value: 'financial', label: 'Financial' }, { value: 'other', label: 'Other' },
];

const stageBadgeVariant = (stage: DealStage) => {
  const map: Record<string, 'info' | 'success' | 'warning' | 'error' | 'primary' | 'default'> = {
    prospecting: 'default', qualification: 'info', proposal: 'info',
    negotiation: 'warning', under_contract: 'info', due_diligence: 'warning',
    closing: 'info', closed_won: 'success', closed_lost: 'error',
  };
  return map[stage] || 'default';
};

const formatCurrency = (v?: number) => {
  if (!v) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};

// A date-only value ("2025-10-22") is a calendar date, not a moment: parsed bare it becomes UTC
// midnight and prints as the PREVIOUS day west of Greenwich (the header said "Opened 10/21" while
// the cockpit said "Oct 22", 2026-09-03). Timestamps keep the local rendering.
const formatDate = (d?: string) => {
  if (!d) return '-';
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return new Date(`${d}T00:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC' });
  return new Date(d).toLocaleDateString();
};
const formatDateTime = (d?: string) => d ? new Date(d).toLocaleString() : '-';
const formatFileSize = (bytes?: number) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function DealDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DealTab>('overview');
  /** OO3b: the property thumb beside the title. It arrives from the cockpit's payload, and it
   *  lives HERE rather than in the cockpit so it survives a tab change that unmounts it. */
  const [headerPhoto, setHeaderPhoto] = useState<string | null>(null);
  const [deal, setDeal] = useState<CrmDeal | null>(null);
  /**
   * ⭐ What the rail tells the model about this page - built from the deal ALREADY LOADED, never
   * a second fetch. That is what makes the block agree with the screen.
   */
  const dealSubject: PageSubject | null = deal
    ? {
        type: 'crm_deal',
        id: deal.id,
        label: deal.deal_name,
        facts: [
          { label: 'Type', value: deal.deal_type },
          { label: 'Stage', value: deal.stage },
          ...(deal.deal_value
            ? [{ label: 'Value', value: Number(deal.deal_value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) }]
            : []),
          ...(deal.expected_close_date ? [{ label: 'Expected close', value: deal.expected_close_date }] : []),
          /**
           * ⭐ The 113th's review: the model is told an address is "needed when there is no record on screen" - and a
           *   record IS on screen here. Told the deal owns no building, it has no reason to hold his back.
           */
          ...(deal.property_id || deal.master_property_id
            ? []
            : [{ label: 'Property', value: 'none attached - the letter takes the building he names' }]),
        ],
        // ⛔ The 113th: a deal with no property owns no building, so the building he states in the chat is the letter's.
        ownsBuilding: Boolean(deal.property_id || deal.master_property_id),
      }
    : null;
  useSetPageSubject(dealSubject);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<CrmDeal>>({});
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityForm, setActivityForm] = useState({
    activity_type: 'note' as ActivityType, subject: '', description: '',
  });
  const [stageChangeNotes, setStageChangeNotes] = useState('');
  const [showStageModal, setShowStageModal] = useState(false);
  const [newStage, setNewStage] = useState<DealStage>('prospecting');

  // Deal Room state
  const [roomDocs, setRoomDocs] = useState<DealRoomDocument[]>([]);
  /** The document open in the in-app viewer, or null. Clicking a title opens this rather than
   *  punting to a new browser tab - the tab remains available from inside the viewer. */
  /** `notice`: why the viewer was opened instead of the PDF he asked for (LOI-PDF1b) - the viewer says it itself. */
  const [viewing, setViewing] = useState<{ id: string; title: string; notice?: string } | null>(null);
  const [roomInvites, setRoomInvites] = useState<DealRoomInvite[]>([]);
  const [roomActivity, setRoomActivity] = useState<DealRoomActivityEntry[]>([]);
  const [roomLoading, setRoomLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadCategory, setUploadCategory] = useState<DealRoomDocumentCategory>('other');
  const [showLoiDialog, setShowLoiDialog] = useState(false);
  const [showProposalDialog, setShowProposalDialog] = useState(false);
  const [showBrochureDialog, setShowBrochureDialog] = useState(false);
  /**
   * 🚨 WHAT THE DEAL'S PROPERTY CAN FILL IN. This page passed NO prefill at all, so the editor
   * opened empty on the one screen where the brochure is actually FILED - the operator retyped
   * an address the record already holds, and the hero photo, the site plan page and the AREA
   * DEMOGRAPHICS block reached nobody here either.
   *
   * ⚠️ A deal may point at EITHER property table (`master_property_id` -> `master_properties`,
   * `property_id` -> the analyzer's `properties`), and it may point at neither. The prefill
   * endpoint resolves either id; a deal with no property simply opens the form empty, as before.
   */
  const [brochurePrefill, setBrochurePrefill] = useState<BrochurePrefill | undefined>(undefined);
  /**
   * 🚨 THE LISTING PROPOSAL DIALOG ACCEPTS A `prefill` AND THIS PAGE PASSED NONE. Found by opening
   * it on the Aguilera deal: owner, address, city, property type and zoning were all placeholder
   * grey, on the one screen where the proposal is FILED against a real client deal. The prop had
   * no supplier here - the same shape of defect as an endpoint with no caller.
   *
   * ⛔ Address, city and zoning only. The OWNER is not on the property record and the price is a
   * recommendation, so both stay his to type.
   */
  const [proposalPrefill, setProposalPrefill] = useState<ProposalPrefill | undefined>(undefined);
  /** The building's identity, for the proposal form to show only when its own read fails (DOC-READ review, minor 7). */
  const [proposalFallback, setProposalFallback] = useState<ProposalPrefill | undefined>(undefined);
  const [loiPrefill, setLoiPrefill] = useState<LoiPrefill | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', expires_in_days: 30 });
  const [showInviteForm, setShowInviteForm] = useState(false);

  // Tasks state
  const [dealTasks, setDealTasks] = useState<DealTask[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [playbooks, setPlaybooks] = useState<TaskPlaybook[]>([]);
  const [showPlaybookModal, setShowPlaybookModal] = useState(false);
  const [selectedPlaybook, setSelectedPlaybook] = useState('');
  const [showAddTaskForm, setShowAddTaskForm] = useState(false);
  const [newTaskForm, setNewTaskForm] = useState({ title: '', description: '', stage: '', due_date: '' });
  const [officialChecklist, setOfficialChecklist] = useState<OfficialChecklistInfo | null>(null);
  const [applyingOfficial, setApplyingOfficial] = useState(false);

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.getCrmDeal(id);
      setDeal(res.deal);
      setForm(res.deal);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load deal');
    }
  }, [id]);

  const loadDealRoom = useCallback(async () => {
    if (!id) return;
    setRoomLoading(true);
    try {
      const res = await api.getDealRoom(id);
      setRoomDocs(res.documents || []);
      setRoomInvites(res.invites || []);
      setRoomActivity(res.activity || []);
    } catch (err) {
      console.error('Failed to load deal room:', err);
    } finally {
      setRoomLoading(false);
    }
  }, [id]);

  const loadTasks = useCallback(async () => {
    if (!id) return;
    setTasksLoading(true);
    try {
      const res = await api.listDealTasks(id);
      setDealTasks(res.tasks || []);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    } finally {
      setTasksLoading(false);
    }
  }, [id]);

  const loadOfficialChecklist = useCallback(async () => {
    if (!id) return;
    try { setOfficialChecklist(await api.getOfficialChecklist(id)); }
    catch (err) { console.error('Failed to load official checklist:', err); setOfficialChecklist(null); }
  }, [id]);

  const handleApplyOfficialChecklist = async () => {
    if (!id) return;
    setApplyingOfficial(true);
    try {
      await api.applyOfficialChecklist(id);
      await Promise.all([loadTasks(), loadOfficialChecklist()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply checklist');
    } finally {
      setApplyingOfficial(false);
    }
  };

  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<{ available: boolean; contact_name: string | null } | null>(null);
  const syncTasks = async () => {
    if (!id) return;
    setSyncMsg('Syncing...');
    try {
      const r = await api.syncDealTasks(id);
      setSyncMsg(r.synced ? `Synced - pulled ${r.pulled ?? 0}, pushed ${r.pushed ?? 0}.` : (r.reason || 'Nothing to sync.'));
      await loadTasks();
    } catch (e) { setSyncMsg(e instanceof Error ? e.message : 'Sync failed'); }
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [id, reload]);

  // A different deal opens on its Overview. This component instance is reused across
  // /crm/deals/:id routes, so without this the tab you left on the last deal stayed active
  // (his oddity, 2026-09-02).
  // Opening another deal resets the tab (OO3) and the header thumb with it - otherwise the
  // previous deal's building sits beside the new deal's name until the cockpit reloads.
  useEffect(() => { setActiveTab('overview'); setHeaderPhoto(null); }, [id]);
  /**
   * ⛔⛔ B2 - A DOCUMENT DIALOG BELONGS TO THE DEAL IT WAS OPENED ON. Left open across a deal change, Generate filed deal
   *    A's letter in deal B's room (probed: open the LOI on d-1, move to d-2, Generate called generateLoi(..., 'd-2')),
   *    with A's changes and A's what-if price along. Each closes and its prefill goes; the LOI dialog is also keyed by
   *    the deal (below), so nothing it held survives into the next deal's letter. The listing proposal and the brochure
   *    open and file the same way, so they close the same way.
   */
  useEffect(() => {
    setShowLoiDialog(false);
    setLoiPrefill(undefined);
    setShowProposalDialog(false);
    setProposalPrefill(undefined);
    setProposalFallback(undefined);
    setShowBrochureDialog(false);
    setBrochurePrefill(undefined);
  }, [id]);
  /** The deal this page shows NOW - for an answer that arrives after he may have moved to another one (B2, D7). */
  const currentId = useRef(id);
  currentId.current = id;

  useEffect(() => {
    if (activeTab === 'deal-room') loadDealRoom();
    if (activeTab === 'tasks') {
      loadTasks();
      loadOfficialChecklist();
      if (id) api.getDealSyncStatus(id).then(setSyncStatus).catch(() => setSyncStatus(null));
    }
  }, [activeTab, loadDealRoom, loadTasks, loadOfficialChecklist, id]);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const { assigned_user, contacts, activities, stage_history, created_at, updated_at, created_by, id: _, is_deleted, stage_entered_at, ...updates } = form as any;
      await api.updateCrmDeal(id, updates);
      await reload();
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Delete this deal?')) return;
    try {
      await api.deleteCrmDeal(id);
      navigate('/crm/deals');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleStageChange = async () => {
    if (!id) return;
    try {
      await api.updateCrmDealStage(id, newStage, stageChangeNotes || undefined);
      setShowStageModal(false);
      setStageChangeNotes('');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update stage');
    }
  };

  const handleAddActivity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      await api.createActivity({ ...activityForm, deal_id: id });
      setShowActivityModal(false);
      setActivityForm({ activity_type: 'note', subject: '', description: '' });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add activity');
    }
  };

  const handleRemoveContact = async (dcId: string) => {
    if (!id || !confirm('Remove this contact from the deal?')) return;
    try {
      await api.removeDealContact(id, dcId);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove contact');
    }
  };

  // Deal Room handlers
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!id || !e.target.files || e.target.files.length === 0) return;
    const files = Array.from(e.target.files);
    setUploadingFile(true);
    try {
      for (const file of files) { await api.uploadDealRoomFile(id, file, uploadCategory); }
      await loadDealRoom();
      setUploadCategory('other');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDoc = async (docId: string) => {
    if (!id || !confirm('Remove this document from the deal? The file itself is kept.')) return;
    try {
      await api.deleteDealRoomDocument(id, docId);
      await loadDealRoom();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  /** The deal's suggestions are a convenience, never a gate - if the prefill call fails the
   *  dialog still opens with an empty form. */
  /**
   * @param fromChat Terms the operator stated to the AI rail ("the LOI at $3.4M, EMD $50,000").
   *
   * ⭐⭐ THEY OVERRIDE THE RECORD, and that ordering is the whole point. The deal's stored price is
   *    a starting position; a price he just said out loud is the offer he means to make right now.
   *    Spreading them last is what makes "generate the LOI at $3.4M" produce $3.4M rather than
   *    silently showing the deal's own figure under a sentence agreeing with him.
   * ⛔ Only fields he STATED arrive here - `appAssistantActions.ts` drops anything the model
   *   invented, malformed or non-positive, so an absent key is a genuine gap and stays blank.
   */
  const openLoiDialog = async (fromChat: PageActionFields = {}) => {
    if (!id) return;
    // ⛔ B2: the deal this letter is for. If he moves to another deal while its facts load, it never opens there.
    const forDeal = id;
    try {
      const res = await api.getDealLoiPrefill(id);
      if (currentId.current !== forDeal) return;
      const p = res.prefill;
      setLoiPrefill({
        address: p.property?.address ?? null,
        city: p.property?.city ?? null,
        state: p.property?.state ?? 'CA',
        zip: p.property?.zip ?? null,
        buildingSf: p.property?.buildingSf ?? null,
        county: p.property?.county ?? null,
        // AIW1-F6 (the 117th, his "prefill from the record"): the record's acreage and land SF fill the boxes as a PAIR,
        //   so the RE line states them; what he says in the chat still beats them (the spread below).
        ...recordSiteSize(p.property, fromChat),
        purchasePrice: p.terms.purchasePrice,
        // ⛔ THE DEAL's price, for the what-if's note. The chat's terms below may replace `purchasePrice` for this
        //   letter; nothing may replace this one (the chat has no field of that name).
        dealPrice: p.terms.purchasePrice,
        emdAmount: p.terms.emdAmount,
        // DOC3: the OM's listing side. Suggestions - the dialog labels them as unconfirmed.
        sellersBrokerage: p.sellersBrokerage ?? null,
        listingAttn: p.listingAttn ?? null,
        listingBroker: p.listingBroker ?? null,
        brokerSource: p.brokerSource ?? null,
        ...fromChat,
        // ⭐ Spec 3.9, LAST so nothing else can stand in for it: the property record, for the letter to say where it differs.
        recordFacts: p.property ? recordFactsOf(p.property) : undefined,
      });
    } catch {
      if (currentId.current !== forDeal) return;
      // ⚠️ The record's prefill failed, but what he SAID is still good. Dropping it here would
      // lose the terms he just typed as well as the ones the deal knows.
      setLoiPrefill(Object.keys(fromChat).length ? { ...fromChat } : undefined);
    }
    setShowLoiDialog(true);
  };

  /**
   * ⛔⛔ I1 - "MAKE THIS THE DEAL'S NUMBER" WROTE `offer_price` FROM INSIDE THE LOI DIALOG. This page's copy moves with
   *    it - `deal`, which the Overview shows, AND `form`, which Save sends WHOLE: left stale, the next routine Save wrote
   *    the old price back over his promotion, silently. Patched in place rather than re-read, so an edit he has open
   *    keeps his other changes.
   */
  /**
   * ⛔⛔ B1 - AND ONLY ON THE DEAL IT WAS MADE ON. This page is reused across /crm/deals/:id and the answer can arrive
   *    after he has moved on: it patched whichever deal the page held THEN, and that deal's next Save wrote the price
   *    (probed: promote on d-1, move to d-2, answer - d-2 showed and saved d-1's price). So the check is made INSIDE the
   *    updaters, against the deal the state holds when the update applies - never against this render's `id`.
   */
  const takeDealPrice = (offerPrice: number, forDeal: string) => {
    setDeal((d) => (d && d.id === forDeal ? { ...d, offer_price: offerPrice } : d));
    setForm((f) => (f.id === forDeal ? { ...f, offer_price: offerPrice } : f));
    // ⛔ D7: the open letter's own copy of the deal's price too - a counter read remounts the form, and the what-if's note
    //   fell back to this copy ("the deal's price stays" the OLD price). Only while the page still shows that deal: the
    //   prefill has no id of its own, and after he moves on it is the next deal's (B2).
    if (currentId.current === forDeal) setLoiPrefill((p) => (p ? { ...p, dealPrice: offerPrice } : p));
  };


  /** Fetch what the deal's property can supply, then open. Same rule as the LOI's prefill above:
   *  a failed fetch still opens the editor, because losing the photos beats refusing to open.
   *  ⭐ DOC-READ (the 157th): the BROCHURE's only now - the listing proposal reads its own (the record AND the
   *  documents filed on it) through its `recordId`. It used to take three boxes from here (address, city, zoning);
   *  its own read fills those and the owner block, the type and size, the APN and the year. */
  const loadPropertyPrefill = async (): Promise<void> => {
    const propertyId = deal?.master_property_id ?? deal?.property_id;
    if (!propertyId) return;
    try {
      const res = await api.getBrochurePrefill(propertyId);
      setBrochurePrefill(res.prefill as BrochurePrefill);
    } catch {
      setBrochurePrefill(undefined);
    }
  };

  /** ⛔ Same sequencing as the proposal below: the fetch REPLACES the prefill, so his stated
   *  choices go on AFTER it resolves or they are discarded a moment later. */
  const openBrochureDialog = async (fields?: PageActionFields): Promise<void> => {
    setShowBrochureDialog(true);
    await loadPropertyPrefill();
    if (fields && Object.keys(fields).length) {
      setBrochurePrefill((prev) => ({ ...(prev ?? {}), ...fields } as BrochurePrefill));
    }
  };

  /**
   * ⛔⛔ THE RECORD'S PREFILL FIRST AND THE CHAT FIELDS ON TOP (the 98th) - now inside the dialog itself (DOC-READ, the
   *   157th): it reads the deal's building through `recordId` on every open and lays `prefill` - his stated words only -
   *   over it, so nothing here can arrive after the form has drawn its boxes. A fresh open carries only this ask's words.
   */
  const openProposalDialog = async (fields?: PageActionFields): Promise<void> => {
    setProposalPrefill(fields && Object.keys(fields).length ? { ...fields } : undefined);
    setShowProposalDialog(true);
    // The review's minor 7: the building's address, city and zoning - the boxes this page filled before DOC-READ - shown
    //   only if the form's own read fails. A deal with no building has none to give.
    setProposalFallback(undefined);
    const forDeal = id;
    const propertyId = deal?.master_property_id ?? deal?.property_id;
    if (!propertyId) return;
    try {
      const p = (await api.getBrochurePrefill(propertyId)).prefill as BrochurePrefill;
      if (currentId.current === forDeal) setProposalFallback({ addressLine: p.addressLine, city: p.city, zoning: p.zoning });
    } catch {
      // Nothing to fall back on; the form's own note says when its read failed too.
    }
  };
  /**
   * The AI rail's doors onto this page's own dialogs - his ask of 2026-09-14, "ask ai in the chat
   * to create a brochure or create an loi".
   *
   * ⭐ THEY CALL THE SAME FUNCTIONS THE BUTTONS CALL. A chat sentence reaches exactly as far as a
   *   click: the dialog opens, and the Generate button inside it is still the only thing that
   *   builds or files a document.
   */
  usePageAction('open_brochure_editor', (fields) => { void openBrochureDialog(fields); });
  usePageAction('open_loi_dialog', (fields) => { void openLoiDialog(fields); });
  usePageAction('open_listing_proposal_dialog', (fields) => { void openProposalDialog(fields); });

  const handleToggleShare = async (doc: DealRoomDocument) => {
    if (!id) return;
    try {
      await api.setDealRoomDocumentSharing(id, doc.id, !doc.shared);
      await loadDealRoom();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update sharing');
    }
  };

  /**
   * The escape hatch, and the Download button inside the viewer.
   *
   * ⚠️ The tab is opened SYNCHRONOUSLY inside the click, before the await, or the popup blocker
   * kills it - that is why this cannot simply await the URL and then call window.open.
   */
  const handleOpenDoc = async (docId: string) => {
    if (!id) return;
    const w = window.open('', '_blank');
    try {
      const res = await api.downloadDealRoomDocument(id, docId);
      if (w) w.location.href = res.url; else window.open(res.url, '_blank');
    } catch (err) {
      if (w) w.close();
      setError(err instanceof Error ? err.message : 'Failed to open document');
    }
  };

  const handleCreateInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !inviteForm.email) return;
    try {
      await api.createDealRoomInvite(id, inviteForm);
      setInviteForm({ email: '', name: '', expires_in_days: 30 });
      setShowInviteForm(false);
      await loadDealRoom();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invite');
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    if (!id || !confirm('Revoke this invite?')) return;
    try {
      await api.revokeDealRoomInvite(id, inviteId);
      await loadDealRoom();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke invite');
    }
  };

  const copyInviteLink = (token: string) => {
    // ⛔ The CLIENT origin, never this page's own - this link is sent to a counterparty.
    const url = clientLinkUrl(`/deal-room/${token}`);
    navigator.clipboard.writeText(url);
  };

  // Task handlers
  const handleToggleTask = async (task: DealTask) => {
    if (!id) return;
    try {
      await api.updateDealTask(id, task.id, { is_completed: !task.is_completed });
      await loadTasks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!id || !confirm('Delete this task?')) return;
    try {
      await api.deleteDealTask(id, taskId);
      await loadTasks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete task');
    }
  };

  const handleApplyPlaybook = async () => {
    if (!id || !selectedPlaybook) return;
    try {
      await api.applyPlaybook(id, selectedPlaybook);
      setShowPlaybookModal(false);
      setSelectedPlaybook('');
      await loadTasks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply playbook');
    }
  };

  const openPlaybookModal = async () => {
    try {
      const res = await api.listPlaybooks();
      setPlaybooks(res.playbooks || []);
      setShowPlaybookModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load playbooks');
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !newTaskForm.title) return;
    try {
      await api.createDealTask(id, {
        title: newTaskForm.title,
        description: newTaskForm.description || undefined,
        stage: newTaskForm.stage || undefined,
        due_date: newTaskForm.due_date || undefined,
      });
      setNewTaskForm({ title: '', description: '', stage: '', due_date: '' });
      setShowAddTaskForm(false);
      await loadTasks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add task');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        </div>
      </Layout>
    );
  }

  if (!deal) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-gray-500">{error || 'Deal not found'}</p>
            <Button className="mt-4" onClick={() => navigate('/crm/deals')}>Back to Deals</Button>
          </div>
        </div>
      </Layout>
    );
  }

  const activeStages = STAGES.filter(s => s.value !== 'closed_lost');
  const currentIdx = activeStages.findIndex(s => s.value === deal.stage);

  // Task stats
  const completedTasks = dealTasks.filter(t => t.is_completed).length;
  const totalTasks = dealTasks.length;
  const taskProgress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const dealTypeLabel = DEAL_TYPES.find(t => t.value === deal?.deal_type)?.label || deal?.deal_type || 'deal';

  // Group tasks by stage
  const tasksByStage = dealTasks.reduce<Record<string, DealTask[]>>((acc, t) => {
    const key = t.stage || 'general';
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});

  return (
    <Layout>
      {/* ===== Header (OO3): breadcrumb, pills, the Oswald title, one meta line, the one red action ===== */}
      <div className="space-y-4">
        <Breadcrumbs items={[
          { label: 'Deals', href: '/crm/deals' },
          { label: STAGES.find(s => s.value === deal.stage)?.label || deal.stage },
          { label: deal?.deal_name || 'Deal' },
        ]} />
        <div className="flex flex-wrap items-start gap-x-[18px] gap-y-3">
          {/* OO3b: the design's 96x66 property thumb beside the title. The header renders from
              `deal` alone and the photo lives on the COCKPIT's property payload, which loads
              separately - so the cockpit hands it up (`onPropertyPhoto`) and the state lives
              here, which is what keeps the thumb through a tab change that unmounts the cockpit.
              ⭐ The box is reserved on first paint and the image fades in, so the title does not
              jump when the payload lands. A deal with no linked property, or one whose property
              has no photo, keeps the title-only header rather than showing an empty frame. */}
          {headerPhoto && (
            <div className="h-[66px] w-[96px] shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
              <img
                src={headerPhoto}
                alt=""
                className="h-full w-full object-cover opacity-0 transition-opacity duration-300"
                onLoad={(e) => { e.currentTarget.style.opacity = '1'; }}
                onError={(e) => { e.currentTarget.closest('div')?.remove(); }}
              />
            </div>
          )}
          <div className="min-w-0 flex-1 basis-[320px]">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Badge variant="dark" className="uppercase tracking-[.14em] text-[10px]">{DEAL_TYPES.find(t => t.value === deal.deal_type)?.label || deal.deal_type}</Badge>
              <Badge variant={stageBadgeVariant(deal.stage)} className="uppercase tracking-[.12em] text-[10px]">{STAGES.find(s => s.value === deal.stage)?.label || deal.stage}</Badge>
              {(deal.priority === 'urgent' || deal.priority === 'high') && (
                <Badge variant="primary" className="uppercase tracking-[.12em] text-[10px]">{deal.priority} priority</Badge>
              )}
            </div>
            <h1 className="break-words font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">{deal.deal_name}</h1>
            <div className="mt-[7px] text-[13.5px] leading-snug text-gray-700">
              {[
                deal.deal_value ? `Deal value ${formatCurrency(deal.deal_value)}` : null,
                deal.escrow_opened_date ? `Opened ${formatDate(deal.escrow_opened_date)}` : null,
                deal.expected_close_date ? `COE ${formatDate(deal.expected_close_date)}` : null,
                deal.assigned_user?.full_name ? `Assigned to ${deal.assigned_user.full_name}` : null,
              ].filter(Boolean).join(' · ') || 'No terms entered yet'}
            </div>
          </div>
          <div className="flex min-w-0 flex-wrap gap-2 sm:shrink-0">
            <Button onClick={() => { setNewStage(deal.stage); setShowStageModal(true); }}>
              Move stage
            </Button>
            <Button variant="outline" onClick={() => setActiveTab('deal-room')}>Share client page</Button>
            {editing ? (
              <>
                <Button variant="outline" onClick={() => { setEditing(false); setForm(deal); }}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setEditing(true)}>Edit</Button>
                <Button variant="ghost" onClick={handleDelete} className="text-gray-500 hover:text-primary-500">Delete</Button>
              </>
            )}
          </div>
        </div>

        {/* Stage stepper card - click a stage to open Move Stage with it preselected */}
        {deal.stage !== 'closed_lost' ? (
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white px-[22px] py-[18px]">
            <div className="flex min-w-[640px] items-start">
              {activeStages.map((s, idx) => {
                const done = currentIdx > idx;
                const now = currentIdx === idx;
                return (
                  <div key={s.value} className={`flex items-start ${idx < activeStages.length - 1 ? 'flex-1' : ''}`}>
                    <button
                      type="button"
                      onClick={() => { setNewStage(s.value); setShowStageModal(true); }}
                      className="group flex w-[74px] shrink-0 flex-col items-center gap-[7px]"
                      title={`Move to ${s.label}`}
                    >
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold leading-none transition-colors ${
                        done ? 'bg-gray-900 text-white'
                          : now ? 'bg-primary-500 text-white shadow-[0_0_0_4px_rgba(206,1,31,.14)]'
                          : 'border-[1.5px] border-gray-200 bg-white text-gray-400 group-hover:border-gray-400'
                      }`}>
                        {done ? '✓' : idx + 1}
                      </span>
                      <span className={`text-center text-[10px] uppercase leading-tight tracking-[.06em] ${
                        now ? 'font-bold text-primary-500' : done ? 'font-medium text-gray-500' : 'font-medium text-gray-400 group-hover:text-gray-600'
                      }`}>{s.label}</span>
                    </button>
                    {idx < activeStages.length - 1 && (
                      <span className={`mt-[9px] h-0.5 flex-1 ${done ? 'bg-gray-900' : now ? 'bg-primary-500' : 'bg-gray-200'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div>
            <Badge variant="default" className="uppercase tracking-[.12em] text-[10px]">Closed - lost</Badge>
          </div>
        )}

        {/* NN0: the property page's actions, on the deal, on every tab. */}
        {id && (
          <DealActionStrip
            dealId={id}
            propertyId={deal.property_id ?? null}
            onLoi={openLoiDialog}
            onProposal={openProposalDialog}
            onBrochure={openBrochureDialog}
          />
        )}

        {/* Tab Navigation */}
        <div role="tablist" className="-mb-px flex gap-0.5 overflow-x-auto border-b border-gray-200">
          {[
            { key: 'overview' as DealTab, label: 'Overview' },
            { key: 'deal-room' as DealTab, label: 'Deal Room' },
            { key: 'tasks' as DealTab, label: `Tasks${totalTasks > 0 ? ` (${completedTasks}/${totalTasks})` : ''}` },
            { key: 'deadlines' as DealTab, label: 'Deadlines' },
          ].map(tab => (
            <button
              key={tab.key}
              role="tab"
              type="button"
              aria-selected={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`-mb-px shrink-0 border-b-2 px-3.5 py-3 text-[13px] leading-none transition-colors ${
                activeTab === tab.key
                  ? 'border-primary-500 font-semibold text-gray-900'
                  : 'border-transparent font-medium text-gray-500 hover:text-gray-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mt-4">
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* OVERVIEW TAB */}
      {/* ============================================================ */}
      {activeTab === 'overview' && (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {id && <div className="lg:col-span-3"><DealCockpit dealId={id} onShare={() => setActiveTab('deal-room')} onPropertyPhoto={setHeaderPhoto} /></div>}
          <div className="lg:col-span-2 space-y-4">
            {/* AIW2 - which Property Database record the deal's row is; never asked of a deal that already holds one. */}
            {deal.property_id && !deal.master_property_id && <MasterLinkPrompt key={deal.property_id} propertyId={deal.property_id} onLinked={reload} />}
            {/* NN0: the deal's property, or the doors to attach one. */}
            {id && (
              <DealPropertyPanel
                dealId={id}
                propertyId={deal.property_id ?? null}
                masterPropertyId={deal.master_property_id ?? null}
                onAttached={reload}
              />
            )}
            {/* Deal Info */}
            <Card>
              <CardHeader>
                <CardTitle>Deal Information</CardTitle>
                <p className="text-xs text-gray-400 mt-0.5">Terms & structure — hit Edit to change them.</p>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Deal Name</label>
                      <input value={form.deal_name || ''} onChange={(e) => setForm(f => ({ ...f, deal_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Deal Type</label>
                      <select value={form.deal_type || 'sale'} onChange={(e) => setForm(f => ({ ...f, deal_type: e.target.value as CrmDealType }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        {DEAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      <select value={form.priority || 'medium'} onChange={(e) => setForm(f => ({ ...f, priority: e.target.value as DealPriority }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Deal Value ($)</label>
                      <input type="number" value={form.deal_value || ''} onChange={(e) => setForm(f => ({ ...f, deal_value: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Probability %</label>
                      <input type="number" min="0" max="100" value={form.probability_percent ?? ''} onChange={(e) => setForm(f => ({ ...f, probability_percent: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Asking Price</label>
                      <input type="number" value={form.asking_price || ''} onChange={(e) => setForm(f => ({ ...f, asking_price: parseFloat(e.target.value) || undefined }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Offer Price</label>
                      <input type="number" value={form.offer_price || ''} onChange={(e) => setForm(f => ({ ...f, offer_price: parseFloat(e.target.value) || undefined }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Expected Close</label>
                      <input type="date" value={form.expected_close_date?.split('T')[0] || ''} onChange={(e) => setForm(f => ({ ...f, expected_close_date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Commission %</label>
                      <input type="number" step="0.01" value={form.commission_percent || ''} onChange={(e) => setForm(f => ({ ...f, commission_percent: parseFloat(e.target.value) || undefined }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea value={form.description || ''} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </div>
                ) : (
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                    {[
                      ['Deal Type', deal.deal_type],
                      ['Priority', deal.priority],
                      ['Probability', deal.probability_percent != null ? `${deal.probability_percent}%` : '-'],
                      ['Asking Price', formatCurrency(deal.asking_price)],
                      ['Offer Price', formatCurrency(deal.offer_price)],
                      ['Final Price', formatCurrency(deal.final_price)],
                      ['Commission', deal.commission_percent ? `${deal.commission_percent}%` : '-'],
                      ['Commission Total', formatCurrency(deal.commission_total)],
                      ['Expected Close', formatDate(deal.expected_close_date)],
                      ['Actual Close', formatDate(deal.actual_close_date)],
                      ['Listing Date', formatDate(deal.listing_date)],
                      ['Assigned To', deal.assigned_user?.full_name || '-'],
                    ].map(([label, val]) => (
                      <div key={label as string}>
                        <dt className="text-sm text-gray-500">{label}</dt>
                        <dd className="text-sm font-medium text-gray-900 mt-0.5">{val || '-'}</dd>
                      </div>
                    ))}
                    {deal.description && (
                      <div className="col-span-2">
                        <dt className="text-sm text-gray-500">Description</dt>
                        <dd className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap">{deal.description}</dd>
                      </div>
                    )}
                  </dl>
                )}
              </CardContent>
            </Card>

            {/* Activities */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Activities</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => setShowActivityModal(true)}>+ Log Activity</Button>
                </div>
              </CardHeader>
              <CardContent>
                {!deal.activities?.length ? (
                  <p className="text-xs text-gray-400 py-1">Nothing logged yet — calls, emails and notes land here.</p>
                ) : (
                  <div className="space-y-3">
                    {deal.activities.map((a: Activity) => (
                      <div key={a.id} className="flex gap-3 border-b border-gray-100 pb-3 last:border-0">
                        <div className="mt-0.5 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-900">{a.subject}</span>
                            <Badge size="sm">{a.activity_type?.replace(/_/g, ' ')}</Badge>
                          </div>
                          {a.description && <p className="text-sm text-gray-600 mt-0.5">{a.description}</p>}
                          <p className="text-xs text-gray-400 mt-1">{formatDateTime(a.activity_date)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stage History */}
            <Card>
              <CardHeader>
                <CardTitle>Stage History</CardTitle>
                <p className="text-xs text-gray-400 mt-0.5">Every stage move, with the why.</p>
              </CardHeader>
              <CardContent>
                {!deal.stage_history?.length ? (
                  <p className="text-xs text-gray-400 py-1">No stage moves yet — click a stage above to start the trail.</p>
                ) : (
                  <div className="space-y-3">
                    {deal.stage_history.map((h: DealStageHistoryEntry) => (
                      <div key={h.id} className="flex items-start gap-3 border-b border-gray-100 pb-3 last:border-0">
                        <div className="mt-0.5 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-info" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-sm">
                            {h.from_stage && (
                              <>
                                <Badge size="sm" variant={stageBadgeVariant(h.from_stage)}>{h.from_stage.replace(/_/g, ' ')}</Badge>
                                <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                              </>
                            )}
                            <Badge size="sm" variant={stageBadgeVariant(h.to_stage)}>{h.to_stage.replace(/_/g, ' ')}</Badge>
                          </div>
                          {h.notes && <p className="text-sm text-gray-600 mt-1">{h.notes}</p>}
                          <p className="text-xs text-gray-400 mt-1">
                            {h.changed_by_user?.full_name || 'System'} &middot; {formatDateTime(h.changed_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Contacts ({deal.contacts?.length || 0})</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {!deal.contacts?.length ? (
                  <p className="text-xs text-gray-400 py-1">No contacts on this deal yet.</p>
                ) : (
                  <div className="space-y-3">
                    {deal.contacts.map((dc: DealContactLink) => (
                      <div key={dc.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="cursor-pointer hover:text-primary-600" onClick={() => dc.contact && navigate(`/crm/contacts/${dc.contact.id}`)}>
                            <p className="font-medium text-sm text-gray-900">{dc.contact?.first_name} {dc.contact?.last_name}</p>
                            {dc.contact?.email && <p className="text-xs text-gray-500">{dc.contact.email}</p>}
                          </div>
                          <button onClick={() => handleRemoveContact(dc.id)} className="text-gray-400 hover:text-red-500" title="Remove">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                        <Badge size="sm" className="mt-1">{dc.role?.replace(/_/g, ' ')}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/*
              Everything filed against this deal (`file_links.target_type = 'deal'`): AIR contracts, PSAs, LOIs,
              analyses and (AIW2) OMs, each named by its kind, contracts first, with ready-to-click signed URLs.
              ⛔ FILED-LABEL (the 119th): titled "Contracts" while it listed all of them - a control names its contents.
            */}
            {id && (
              <Card>
                <CardHeader><CardTitle>Documents</CardTitle></CardHeader>
                <CardContent><DealContracts dealId={id} /></CardContent>
              </Card>
            )}

            <Card>
              <CardHeader><CardTitle>Key Dates</CardTitle></CardHeader>
              <CardContent>
                <dl className="space-y-2 text-sm">
                  {[
                    ['Created', formatDate(deal.created_at)],
                    ['Stage Entered', formatDate(deal.stage_entered_at)],
                    ['Expected Close', formatDate(deal.expected_close_date)],
                    ['Listing Date', formatDate(deal.listing_date)],
                    ['Expiration', formatDate(deal.expiration_date)],
                  ].map(([label, val]) => (
                    <div key={label as string} className="flex justify-between">
                      <dt className="text-gray-500">{label}</dt>
                      <dd className="text-gray-900">{val}</dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* DEAL ROOM TAB */}
      {/* ============================================================ */}
      {activeTab === 'deal-room' && (
        <div className="mt-4 space-y-4">
          {roomLoading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-500" />
            </div>
          ) : (
            <>
              {/* Documents (OO4): the design's document table - file, category tag, size, uploaded,
                  visibility pill - with the room's own doors in the head. The three generators
                  live in the action strip above the tabs (NN0), not here. */}
              <Card>
                <div className="flex flex-wrap items-center gap-2.5 border-b border-gray-150 px-5 py-4">
                  <h2 className="text-[15px] font-semibold leading-none text-gray-900">Documents</h2>
                  <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-semibold leading-none text-gray-700">
                    {roomDocs.length} file{roomDocs.length === 1 ? '' : 's'}
                    {(() => { const total = roomDocs.reduce((sum, d) => sum + (d.file_size || 0), 0); return total ? ` · ${formatFileSize(total)}` : ''; })()}
                  </span>
                  {/* flex-wrap, and it is load-bearing: this row carries several controls inside a
                      Card with `overflow-hidden`, so without wrapping the later buttons are
                      CLIPPED rather than pushed to a second line - a button that exists, ships
                      in the bundle, and cannot be clicked. */}
                  <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                    {/* The builder asks which form when opened bare - no formCode default here,
                        for the same reason App.tsx refuses one: a lease and a purchase
                        agreement must never be one wrong click apart. */}
                    <Button size="sm" variant="outline" onClick={() => navigate(`/contracts/${id}`)}>
                      Open Contract Builder
                    </Button>
                    <select
                      value={uploadCategory}
                      onChange={e => setUploadCategory(e.target.value as DealRoomDocumentCategory)}
                      aria-label="Upload category"
                      className="h-[30px] rounded-lg border-gray-300 py-0 pl-2.5 pr-8 text-[11.5px] font-medium text-gray-800 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25"
                    >
                      {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <Button
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingFile}
                    >
                      {uploadingFile ? 'Uploading...' : '+ Upload File'}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                  </div>
                </div>
                {!roomDocs.length ? (
                  <p className="px-5 py-4 text-[12.5px] text-gray-500">No documents yet - upload files to share with your team and external parties.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="min-w-[680px]">
                      <div className="grid grid-cols-[minmax(0,2.4fr)_.9fr_.7fr_.9fr_.9fr_40px] border-b border-gray-150 bg-gray-50 px-5 py-2.5 text-[9.5px] font-bold uppercase leading-none tracking-[.12em] text-gray-500">
                        <div>File</div><div>Category</div><div className="text-right">Size</div><div className="text-right">Uploaded</div><div className="text-right">Visibility</div><div></div>
                      </div>
                      {roomDocs.map(doc => (
                        <div key={doc.id} className="grid grid-cols-[minmax(0,2.4fr)_.9fr_.7fr_.9fr_.9fr_40px] items-center border-b border-gray-100 px-5 py-3 last:border-b-0 hover:bg-gray-50">
                          <div className="flex min-w-0 items-center gap-2.5 pr-3">
                            {doc.status === 'pending' ? (
                              <span className="flex min-w-0 items-center gap-2 text-left" title="Promised but not yet provided">
                                <svg className="h-4 w-4 shrink-0 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="truncate text-[13px] font-medium text-gray-500">{doc.title}</span>
                                <span className="shrink-0 rounded-sm bg-warning-wash px-[7px] py-1 text-[9.5px] font-bold uppercase leading-none tracking-[.1em] text-warning">Expected</span>
                              </span>
                            ) : (
                              <>
                                {/* LOI-PDF1b: where a letter has a PDF the title says what IT opens - the Word file,
                                    in the viewer, as it always did (a control names its contents). */}
                                <button type="button" onClick={() => setViewing({ id: doc.id, title: doc.title })} className="group flex min-w-0 items-center gap-2.5 text-left" title={doc.pdf ? 'Open the Word file' : 'Preview document'}>
                                  <svg className="h-4 w-4 shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" /><polyline points="14 3 14 8 19 8" />
                                  </svg>
                                  <span className="truncate text-[13px] font-medium leading-tight text-primary-500 group-hover:underline underline-offset-[3px]">{doc.title}</span>
                                </button>
                                {/* LOI-PDF1b (the 130th, his yes): the letter as it prints - header, footer and all. The
                                    window opens inside the tap (`OpenPdfButton`). ⛔ A PDF that cannot be made never
                                    leaves him with nothing: the reason is said and the Word file opens in the viewer. */}
                                {doc.pdf && id && (
                                  <span className="shrink-0">
                                    <OpenPdfButton
                                      sign={() => api.dealRoomDocumentPdf(id, doc.id).then((r) => r.url)}
                                      onStart={() => setError(null)}
                                      onFailed={(message) => {
                                        const said = `${message} Showing the Word file instead.`;
                                        setError(said);
                                        // ⭐ And INSIDE the viewer (found by looking): this page's banner is behind it.
                                        setViewing({ id: doc.id, title: doc.title, notice: said });
                                      }}
                                    />
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          <div><Tag>{doc.category || 'other'}</Tag></div>
                          <div className="text-right text-[12.5px] font-medium leading-none tabular-nums text-gray-700">{formatFileSize(doc.file_size ?? undefined)}</div>
                          <div className="text-right text-[12.5px] font-medium leading-none tabular-nums text-gray-700">{formatDate(doc.created_at)}</div>
                          <div className="text-right">
                            <button
                              type="button"
                              onClick={() => handleToggleShare(doc)}
                              title={doc.shared
                                ? 'Visible to invited external parties - click to make internal'
                                : 'Internal only - click to share with invited external parties'}
                            >
                              <Badge variant={doc.shared ? 'success' : 'default'}>{doc.shared ? 'Shared' : 'Internal'}</Badge>
                            </button>
                          </div>
                          <div className="text-right">
                            <button type="button" onClick={() => handleDeleteDoc(doc.id)} className="text-gray-400 hover:text-primary-500" title="Delete" aria-label="Delete document">
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
                {/* External access: the invite empty state from the design, the invite form, the live invites */}
                <Card>
                  <div className="flex items-center gap-2.5 border-b border-gray-150 px-5 py-4">
                    <h2 className="text-[15px] font-semibold leading-none text-gray-900">External access</h2>
                    {roomInvites.length > 0 && <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-semibold leading-none text-gray-700">{roomInvites.length}</span>}
                    <Button size="sm" variant="outline" className="ml-auto" onClick={() => setShowInviteForm(!showInviteForm)}>
                      {showInviteForm ? 'Cancel' : '+ Invite'}
                    </Button>
                  </div>
                  {showInviteForm && (
                    <form onSubmit={handleCreateInvite} className="space-y-3 border-b border-gray-150 bg-gray-50 px-5 py-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="mb-1 block text-[11px] text-gray-500">Email *</label>
                          <input
                            type="email"
                            required
                            value={inviteForm.email}
                            onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                            className="w-full rounded-lg border-gray-300 text-[13px] focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25"
                            placeholder="buyer@example.com"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[11px] text-gray-500">Name</label>
                          <input
                            value={inviteForm.name}
                            onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))}
                            className="w-full rounded-lg border-gray-300 text-[13px] focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25"
                            placeholder="John Doe"
                          />
                        </div>
                      </div>
                      <div className="flex items-end gap-3">
                        <div>
                          <label className="mb-1 block text-[11px] text-gray-500">Expires in (days)</label>
                          <input
                            type="number"
                            min="1"
                            value={inviteForm.expires_in_days}
                            onChange={e => setInviteForm(f => ({ ...f, expires_in_days: parseInt(e.target.value) || 30 }))}
                            className="w-24 rounded-lg border-gray-300 text-[13px] focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25"
                          />
                        </div>
                        <Button type="submit" size="sm">Send Invite</Button>
                      </div>
                    </form>
                  )}
                  {!roomInvites.length ? (
                    <div className="px-6 py-[30px] text-center">
                      <div className="mx-auto mb-3.5 flex h-[38px] w-[38px] items-center justify-center rounded-full bg-gray-100">
                        <svg className="h-[18px] w-[18px] text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 018 0v3" /></svg>
                      </div>
                      <div className="mb-1.5 text-[13.5px] font-semibold leading-snug text-gray-800">No one outside Apex has access</div>
                      <div className="mx-auto mb-4 max-w-[300px] text-[12.5px] leading-relaxed text-gray-500">Invite the buyer, lender or attorney to a secure room. They see only what you mark shared.</div>
                      {!showInviteForm && <Button variant="secondary" onClick={() => setShowInviteForm(true)}>Invite a party</Button>}
                    </div>
                  ) : (
                    <div>
                      {roomInvites.map(inv => (
                        <div key={inv.id} className="flex items-center gap-3 border-b border-gray-100 px-5 py-3.5 last:border-b-0">
                          <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-gray-100 text-[11px] font-bold text-gray-700">
                            {(inv.name || inv.email || '?').trim().split(/\s+/).map((w: string) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[13.5px] font-semibold leading-tight text-gray-800">{inv.name || inv.email}</p>
                            <p className="truncate text-[11.5px] text-gray-500">{inv.email}</p>
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px]">
                              {inv.expires_at && <span className="text-gray-400">Expires {formatDate(inv.expires_at)}</span>}
                              {inv.last_accessed_at && <span className="text-success">Last viewed {formatDateTime(inv.last_accessed_at)}</span>}
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-3">
                            <button
                              type="button"
                              onClick={() => copyInviteLink(inv.access_token)}
                              className="text-[11.5px] font-semibold leading-none text-primary-500 hover:text-primary-600 hover:underline underline-offset-[3px]"
                              title="Copy link"
                            >
                              Copy link
                            </button>
                            <button type="button" onClick={() => handleRevokeInvite(inv.id)} className="text-gray-400 hover:text-primary-500" title="Revoke" aria-label="Revoke invite">
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                {/* Recent activity: the compact timeline */}
                <Card>
                  <div className="flex items-center border-b border-gray-150 px-5 py-4">
                    <h2 className="text-[15px] font-semibold leading-none text-gray-900">Recent activity</h2>
                  </div>
                  <div className="px-5 pb-5 pt-[18px]">
                    {!roomActivity.length ? (
                      <p className="text-[12.5px] text-gray-500">No activity yet - uploads, views and downloads show here.</p>
                    ) : (
                      roomActivity.slice(0, 20).map((a, i, arr) => (
                        <div key={a.id} className="relative flex gap-3 pb-4 last:pb-0">
                          {i < arr.length - 1 && <span className="absolute bottom-0 left-[5px] top-3.5 w-px bg-gray-150" aria-hidden="true" />}
                          <span className={`relative z-[1] mt-[3px] h-[11px] w-[11px] shrink-0 rounded-full ${
                            a.action === 'uploaded' ? 'bg-success' :
                            a.action === 'downloaded' ? 'bg-gray-700' :
                            a.action === 'viewed' ? 'bg-warning' :
                            a.action === 'invited' ? 'bg-primary-500' :
                            String(a.action).startsWith('removed') ? 'bg-primary-500' :
                            'bg-gray-300'
                          }`} />
                          <div className="min-w-0 flex-1">
                            <div className="text-[12.5px] font-medium leading-snug text-gray-800">
                              {/* OO4a: the API resolves who ACTED (`actor_name`) - a member by
                                  their profile name, an external reader by the address on the
                                  row. This used to read the metadata address first, which on an
                                  `invited` row is the person INVITED, so an invite Joseph sent
                                  was credited to the person who received it. */}
                              {a.actor_name || 'User'}{' '}
                              {/* The action is a stored token (`removed_document`); print it as words. */}
                              <span className="text-gray-500">{String(a.action).replace(/_/g, ' ')}</span>
                              {/* The OBJECT of the action: the file, or - on invited/revoked,
                                  which carry no file - the person the invite was for. */}
                              {(a.metadata as any)?.file_name ? (
                                <span className="text-gray-700"> {(a.metadata as any).file_name}</span>
                              ) : a.user_id && ((a.metadata as any)?.name || (a.metadata as any)?.email) ? (
                                <span className="text-gray-700"> {(a.metadata as any).name || (a.metadata as any).email}</span>
                              ) : null}
                            </div>
                            <div className="text-[11px] leading-snug text-gray-400">{formatDateTime(a.created_at)}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              </div>
            </>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* TASKS TAB */}
      {/* ============================================================ */}
      {activeTab === 'deadlines' && id && (
        <div className="mt-4">
          <div className="bg-gray-50 rounded-xl p-4"><DeadlinesPanel dealId={id} escrowOpenedDate={deal?.escrow_opened_date} onAnchorChanged={reload} /></div>
        </div>
      )}

      {activeTab === 'tasks' && (
        <div className="mt-4 space-y-4">
          {tasksLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
            </div>
          ) : (
            <>
              {officialChecklist?.playbook && (
                <div className="flex items-center gap-3 flex-wrap rounded-xl px-4 py-3 border bg-primary-50/60 border-primary-200">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      Official {dealTypeLabel} checklist
                      <span className="text-gray-400 font-normal ml-2">{officialChecklist.playbook.task_count} steps</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {officialChecklist.applied
                        ? `Applied — ${officialChecklist.applied_count} step${officialChecklist.applied_count === 1 ? '' : 's'} on this deal. Re-sync to top up any new steps.`
                        : `Add the standard ${String(dealTypeLabel).toLowerCase()} transaction steps in one tap. They group by stage and sync to GoHighLevel.`}
                    </p>
                  </div>
                  <Button size="sm" onClick={handleApplyOfficialChecklist} disabled={applyingOfficial}>
                    {applyingOfficial ? 'Applying…' : officialChecklist.applied ? 'Re-sync checklist' : `Apply official ${dealTypeLabel} checklist`}
                  </Button>
                </div>
              )}
              <div className={`flex items-center gap-3 flex-wrap rounded-xl px-4 py-3 border ${syncStatus && !syncStatus.available ? 'bg-warning-wash/60 border-warning/30' : 'bg-gray-50/80 border-gray-200/70'}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">GoHighLevel task sync</p>
                  <p className="text-xs text-gray-500">
                    {syncStatus == null ? 'Checking…'
                      : syncStatus.available ? `On — tasks sync with ${syncStatus.contact_name || "this deal's GoHighLevel contact"}.`
                      : "This deal isn't linked to a GoHighLevel contact, so tasks can't sync."}
                  </p>
                </div>
                {syncMsg && <span className="text-xs text-gray-500">{syncMsg}</span>}
                <button onClick={syncTasks} disabled={syncStatus != null && !syncStatus.available} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed">Sync now</button>
              </div>
              {/* Task Progress + Actions */}
              <Card>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium text-gray-700">
                        {completedTasks}/{totalTasks} tasks completed
                      </span>
                      {totalTasks > 0 && (
                        <span className="text-sm text-gray-500">{taskProgress}%</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={openPlaybookModal}>Use a different checklist</Button>
                      <Button size="sm" onClick={() => setShowAddTaskForm(!showAddTaskForm)}>
                        {showAddTaskForm ? 'Cancel' : '+ Add Task'}
                      </Button>
                      <button
                        onClick={() => navigate('/playbooks')}
                        className="text-xs text-primary-500 hover:underline ml-2"
                      >
                        Manage Playbooks
                      </button>
                    </div>
                  </div>
                  {totalTasks > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full transition-all"
                        style={{ width: `${taskProgress}%` }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Add Task Form */}
              {showAddTaskForm && (
                <Card>
                  <CardContent>
                    <form onSubmit={handleAddTask} className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Task Title *</label>
                          <input
                            required
                            value={newTaskForm.title}
                            onChange={e => setNewTaskForm(f => ({ ...f, title: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="e.g., Review PSA"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Stage</label>
                          <select
                            value={newTaskForm.stage}
                            onChange={e => setNewTaskForm(f => ({ ...f, stage: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="">No stage</option>
                            {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Due Date</label>
                          <input
                            type="date"
                            value={newTaskForm.due_date}
                            onChange={e => setNewTaskForm(f => ({ ...f, due_date: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                          <textarea
                            value={newTaskForm.description}
                            onChange={e => setNewTaskForm(f => ({ ...f, description: e.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button type="submit" size="sm">Add Task</Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Tasks List */}
              {!dealTasks.length ? (
                <Card>
                  <CardContent>
                    <p className="text-gray-500 text-sm text-center py-8">
                      No tasks yet.{officialChecklist?.playbook ? ` Apply the official ${dealTypeLabel} checklist above,` : ' Apply a checklist'} or add tasks manually to track deal progress.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                Object.entries(tasksByStage).map(([stage, tasks]) => (
                  <Card key={stage}>
                    <CardHeader>
                      <CardTitle className="text-sm">
                        {stage === 'general' ? 'General' : stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        <span className="text-gray-400 font-normal ml-2">({tasks.filter(t => t.is_completed).length}/{tasks.length})</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {tasks.map(task => (
                          <div
                            key={task.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border ${
                              task.is_completed ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'
                            }`}
                          >
                            <button
                              onClick={() => handleToggleTask(task)}
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                task.is_completed
                                  ? 'bg-primary-500 border-primary-500'
                                  : 'border-gray-300 hover:border-primary-400'
                              }`}
                            >
                              {task.is_completed && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-medium ${task.is_completed ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                                  {task.title}
                                </span>
                                {task.is_required && (
                                  <span className="text-xs text-red-500 font-medium">Required</span>
                                )}
                              </div>
                              {task.description && (
                                <p className={`text-xs mt-0.5 ${task.is_completed ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {task.description}
                                </p>
                              )}
                              <div className="flex items-center gap-3 mt-1">
                                {task.assigned_user && (
                                  <span className="text-xs text-gray-500">
                                    {task.assigned_user.full_name}
                                  </span>
                                )}
                                {task.due_date && (
                                  <span className={`text-xs ${
                                    isTaskOverdue(task.due_date, task.is_completed)
                                      ? 'text-red-500'
                                      : 'text-gray-400'
                                  }`}>
                                    Due: {formatDate(taskDueDate(task.due_date) ?? undefined)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteTask(task.id)}
                              className="text-gray-300 hover:text-red-500 flex-shrink-0"
                              title="Delete task"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* MODALS */}
      {/* ============================================================ */}

      {/* The deal's LOI entry: the letter is FILED here, so it lands in the documents list
          above the moment it is generated. */}
      <LoiGeneratorDialog
        key={id}
        open={showLoiDialog}
        onClose={() => setShowLoiDialog(false)}
        prefill={loiPrefill}
        dealId={id}
        onFiled={loadDealRoom}
        onDealPriceChanged={takeDealPrice}
      />

      {/* The deal's listing-proposal entry. FILED here too, so it lands in the documents list
          the moment it is generated. */}
      <ProposalGeneratorDialog
        open={showProposalDialog}
        onClose={() => setShowProposalDialog(false)}
        /* DOC-READ (the 157th): the form reads the deal's building - its record and the documents filed on it -
           itself; only what he said in the chat is carried in `prefill`. No building, nothing to read. */
        recordId={deal?.master_property_id ?? deal?.property_id ?? null}
        fallback={proposalFallback}
        prefill={proposalPrefill}
        dealId={id}
        onFiled={loadDealRoom}
      />

      {/* The deal's brochure / OM entry. Filed here too, on the explicit save - the live
          preview posts without a deal id, so typing never files. */}
      {viewing && id && (
        <DocumentViewer
          dealId={id}
          docId={viewing.id}
          title={viewing.title}
          notice={viewing.notice ?? null}
          onClose={() => setViewing(null)}
          onDownload={() => handleOpenDoc(viewing.id)}
          // MOB4: on a phone the viewer shares the file itself - a Word file in a window is a white page.
          fileUrl={() => api.downloadDealRoomDocument(id, viewing.id).then((r) => r.url)}
        />
      )}

      <BrochureGeneratorDialog
        open={showBrochureDialog}
        onClose={() => setShowBrochureDialog(false)}
        prefill={brochurePrefill}
        dealId={id}
        onFiled={loadDealRoom}
      />

      {/* Stage Change Modal - OO5d: through the Dialog primitive. */}
      <Dialog
        open={showStageModal}
        onClose={() => setShowStageModal(false)}
        title="Move Deal Stage"
        size="sm"
        footer={<>
          <Button variant="outline" onClick={() => setShowStageModal(false)}>Cancel</Button>
          <Button onClick={handleStageChange}>Move Stage</Button>
        </>}
      >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Stage</label>
                <select value={newStage} onChange={(e) => setNewStage(e.target.value as DealStage)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea value={stageChangeNotes} onChange={(e) => setStageChangeNotes(e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
      </Dialog>

      {/* Add Activity Modal - OO5d: through the Dialog primitive. */}
      <Dialog
        open={showActivityModal}
        onClose={() => setShowActivityModal(false)}
        title="Log Activity"
        size="md"
        onSubmit={handleAddActivity}
        footer={<>
          <Button type="button" variant="outline" onClick={() => setShowActivityModal(false)}>Cancel</Button>
          <Button type="submit">Save Activity</Button>
        </>}
      >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={activityForm.activity_type} onChange={(e) => setActivityForm(f => ({ ...f, activity_type: e.target.value as ActivityType }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  {ACTIVITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input required value={activityForm.subject} onChange={(e) => setActivityForm(f => ({ ...f, subject: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={activityForm.description} onChange={(e) => setActivityForm(f => ({ ...f, description: e.target.value }))} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
      </Dialog>

      {/* Apply Playbook Modal - OO5d: through the Dialog primitive. */}
      <Dialog
        open={showPlaybookModal}
        onClose={() => setShowPlaybookModal(false)}
        title="Apply Playbook"
        size="md"
        footer={<>
          <Button variant="outline" onClick={() => setShowPlaybookModal(false)}>Cancel</Button>
          <Button onClick={handleApplyPlaybook} disabled={!selectedPlaybook}>Apply</Button>
        </>}
      >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Select a playbook template to create tasks for this deal.</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Playbook</label>
                <select
                  value={selectedPlaybook}
                  onChange={e => setSelectedPlaybook(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Select a playbook...</option>
                  {playbooks.map(pb => (
                    <option key={pb.id} value={pb.id}>
                      {pb.name} {pb.deal_type ? `(${pb.deal_type})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              {selectedPlaybook && playbooks.find(p => p.id === selectedPlaybook)?.description && (
                <p className="text-xs text-gray-500">
                  {playbooks.find(p => p.id === selectedPlaybook)?.description}
                </p>
              )}
            </div>
      </Dialog>
    </Layout>
  );
}
